import React, { useState, useEffect, useCallback } from 'react';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import SlipListPage from './pages/SlipListPage';
import SlipDetailPage from './pages/SlipDetailPage';
import PromoManagePage from './pages/PromoManagePage';
import StoreStatusPage from './pages/StoreStatusPage';
import { SlipPayment } from './services/api';

type NavPage = 'dashboard' | 'store' | 'slips' | 'promo';

const COMPACT_BREAKPOINT = 899;

function useIsCompactNav() {
  const [compact, setCompact] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT}px)`).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT}px)`);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return compact;
}

const App: React.FC = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [username, setUsername] = useState(localStorage.getItem('admin_username') ?? '');
  const [nav, setNav] = useState<NavPage>('dashboard');
  const [selectedPayment, setSelectedPayment] = useState<SlipPayment | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isCompact = useIsCompactNav();

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  useEffect(() => {
    if (!token) {
      setSelectedPayment(null);
      closeSidebar();
    }
  }, [token, closeSidebar]);

  useEffect(() => {
    if (!isCompact) closeSidebar();
  }, [isCompact, closeSidebar]);

  useEffect(() => {
    closeSidebar();
  }, [nav, selectedPayment, closeSidebar]);

  useEffect(() => {
    if (!sidebarOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSidebar();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sidebarOpen, closeSidebar]);

  useEffect(() => {
    document.body.style.overflow = isCompact && sidebarOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCompact, sidebarOpen]);

  const handleLogin = (u: string) => {
    setToken(localStorage.getItem('admin_token'));
    setUsername(u);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    setToken(null);
    setUsername('');
    closeSidebar();
  };

  if (!token) return <LoginPage onLogin={handleLogin} />;

  const goNav = (id: NavPage) => {
    setNav(id);
    setSelectedPayment(null);
    closeSidebar();
  };

  return (
    <div className="admin-shell">
      {isCompact && (
        <header className="admin-topbar" role="banner">
          <button
            type="button"
            className="admin-menu-trigger"
            aria-label="เปิดเมนู"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen(true)}
          >
            ☰
          </button>
          <span className="admin-topbar-title">Coffee Admin</span>
          {username ? <span className="admin-topbar-user">{username}</span> : null}
        </header>
      )}

      {isCompact && sidebarOpen && (
        <button type="button" className="admin-backdrop" aria-label="ปิดเมนู" onClick={closeSidebar} />
      )}

      <aside
        id="admin-sidebar"
        aria-label="เมนูหลัก"
        className={`admin-sidebar${isCompact && sidebarOpen ? ' admin-sidebar-open' : ''}`}
      >
        {isCompact && (
          <button type="button" className="admin-sidebar-close" aria-label="ปิดเมนู" onClick={closeSidebar}>
            ×
          </button>
        )}
        <div className="admin-sidebar-header">
          <div style={{ fontSize: 28, marginBottom: 6 }}>☕</div>
          <p style={{ fontWeight: 700, fontSize: 15 }}>Coffee Admin</p>
          <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{username}</p>
        </div>
        <nav className="admin-sidebar-nav">
          {([
            { id: 'dashboard' as NavPage, icon: '📊', label: 'สรุป' },
            { id: 'store' as NavPage, icon: '🚪', label: 'ร้าน เปิด/ปิด' },
            { id: 'slips' as NavPage, icon: '🧾', label: 'จัดการสลิป' },
            { id: 'promo' as NavPage, icon: '🎁', label: 'โปรโมชัน' },
          ]).map(item => (
            <button
              key={item.id}
              type="button"
              className="admin-nav-btn"
              onClick={() => goNav(item.id)}
              style={{
                fontWeight: nav === item.id ? 700 : 500,
                background: nav === item.id ? 'rgba(245,158,11,0.2)' : 'transparent',
                color: nav === item.id ? '#f59e0b' : 'rgba(255,255,255,0.7)',
                marginBottom: 4,
              }}
            >
              <span>{item.icon}</span> {item.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <button
            type="button"
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '12px 14px',
              minHeight: 44,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'transparent',
              color: 'rgba(255,255,255,0.6)',
              fontSize: 14,
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
            }}
          >
            ออกจากระบบ
          </button>
        </div>
      </aside>

      <main className="admin-main">
        {nav === 'dashboard' && (
          <DashboardPage
            onGoSlips={() => goNav('slips')}
            onGoPromo={() => goNav('promo')}
            onGoStore={() => goNav('store')}
          />
        )}
        {nav === 'store' && <StoreStatusPage />}
        {nav === 'slips' && !selectedPayment && <SlipListPage onSelect={p => setSelectedPayment(p)} />}
        {nav === 'slips' && selectedPayment && (
          <SlipDetailPage
            payment={selectedPayment}
            onBack={() => setSelectedPayment(null)}
            onDone={() => setSelectedPayment(null)}
          />
        )}
        {nav === 'promo' && <PromoManagePage />}
      </main>
    </div>
  );
};

export default App;
